import { type JSONContent } from '@tiptap/core';
import { v4 as uuidv4 } from 'uuid';

import { type Board } from '@air/api/types';
import { type WorkspaceAgentClarifyChoice } from '@air/component-agent-clarify';

import { buildWorkspaceAgentComposerSeedContent } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentComposerSeedContent';
import {
  buildWorkspaceAgentGuidedVariantRenameInstruction,
  type WorkspaceAgentGuidedVariantTitleSuffix,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantOutput';

export const WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_QUESTION = 'Where should I save these variants?';

export type WorkspaceAgentGuidedVariantChosenBoard = Pick<Board, 'id' | 'title'>;

const FALLBACK_BOARD_LABEL = 'Board';

export const WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS = {
  NEW_SUBBOARD: 'new-subboard',
  SELECT_BOARD: 'select-board',
  THIS_BOARD: 'this-board',
  VERSIONS: 'versions',
} as const;

export type WorkspaceAgentGuidedVariantSaveOptionId =
  (typeof WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS)[keyof typeof WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS];

type WorkspaceAgentGuidedVariantSaveOption = {
  id: WorkspaceAgentGuidedVariantSaveOptionId;
  label: string;
};

export const WORKSPACE_AGENT_GUIDED_VARIANT_SELECT_BOARD_SAVE_OPTION: WorkspaceAgentGuidedVariantSaveOption = {
  id: WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS.SELECT_BOARD,
  label: 'Select board',
};

export const WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTIONS: readonly WorkspaceAgentGuidedVariantSaveOption[] = [
  { id: WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS.VERSIONS, label: 'Save as versions' },
  { id: WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS.THIS_BOARD, label: 'Save to this board' },
  { id: WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS.NEW_SUBBOARD, label: 'Save to a new subboard' },
  WORKSPACE_AGENT_GUIDED_VARIANT_SELECT_BOARD_SAVE_OPTION,
];

export const isWorkspaceAgentGuidedVariantSaveOptionId = (
  value: string,
): value is WorkspaceAgentGuidedVariantSaveOptionId =>
  WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTIONS.some((option) => option.id === value);

const CURRENT_BOARD_SAVE_OPTION_IDS = new Set<WorkspaceAgentGuidedVariantSaveOptionId>([
  WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS.NEW_SUBBOARD,
  WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS.THIS_BOARD,
]);

/** Save-location choices from {@link getWorkspaceAgentGuidedVariantSaveOptions} — always versions + select board. */
export type WorkspaceAgentGuidedVariantSaveLocationOptions = readonly WorkspaceAgentClarifyChoice[];

export const getWorkspaceAgentGuidedVariantClarifySaveOptions = (
  options: WorkspaceAgentGuidedVariantSaveLocationOptions,
): readonly WorkspaceAgentClarifyChoice[] =>
  options.filter((option) => option.id !== WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS.SELECT_BOARD);

export const getWorkspaceAgentGuidedVariantSelectBoardOptionIndex = (
  options: WorkspaceAgentGuidedVariantSaveLocationOptions,
): number => options.findIndex((option) => option.id === WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION_IDS.SELECT_BOARD);

export type WorkspaceAgentGuidedVariantSaveDestination =
  | { boardId: string; boardTitle: string; type: 'board' }
  | { boardId: string; type: 'new-subboard' }
  | { type: 'versions' };

export const toWorkspaceAgentBoardMoniker = (boardId: string) => `@[board:${boardId}]`;

export const getWorkspaceAgentGuidedVariantSaveOptions = ({
  hasCurrentBoard,
}: {
  hasCurrentBoard: boolean;
}): WorkspaceAgentGuidedVariantSaveLocationOptions =>
  WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTIONS.filter(
    (option) => hasCurrentBoard || !CURRENT_BOARD_SAVE_OPTION_IDS.has(option.id),
  );

export type WorkspaceAgentGuidedVariantSaveInstructionOptions = {
  sizeCount?: number;
};

const hasMultipleSizes = (sizeCount: number | undefined): boolean => typeof sizeCount === 'number' && sizeCount > 1;

export const getWorkspaceAgentGuidedVariantSaveInstruction = (
  destination: WorkspaceAgentGuidedVariantSaveDestination,
  options: WorkspaceAgentGuidedVariantSaveInstructionOptions = {},
): string => {
  switch (destination.type) {
    case 'versions':
      // Multi-size scale/crop and smart resize otherwise ask whether to keep only the first file.
      return hasMultipleSizes(options.sizeCount)
        ? 'Save as new versions of the originals. Save every size as a new version. Do not create new assets. Leave the originals unchanged.'
        : 'Save as new versions of the originals. Do not create new assets. Leave the originals unchanged.';
    case 'board':
      return `Save the results as new assets on ${toWorkspaceAgentBoardMoniker(destination.boardId)}. Leave the originals unchanged.`;
    case 'new-subboard':
      return `Create a new subboard of ${toWorkspaceAgentBoardMoniker(destination.boardId)} and save the results as new assets there. Leave the originals unchanged.`;
    default: {
      const exhaustive: never = destination;

      return exhaustive;
    }
  }
};

export const withWorkspaceAgentGuidedVariantSaveDestination = (
  prompt: string,
  destination: WorkspaceAgentGuidedVariantSaveDestination,
  options: WorkspaceAgentGuidedVariantSaveInstructionOptions = {},
): string => `${prompt} ${getWorkspaceAgentGuidedVariantSaveInstruction(destination, options)}`;

/** Save destination first, then the title sentence — the title instruction always closes the prompt. */
export const withWorkspaceAgentGuidedVariantSaveDestinationAndRename = (
  body: string,
  destination: WorkspaceAgentGuidedVariantSaveDestination,
  renameSuffix: WorkspaceAgentGuidedVariantTitleSuffix,
  options: WorkspaceAgentGuidedVariantSaveInstructionOptions = {},
): string =>
  `${withWorkspaceAgentGuidedVariantSaveDestination(body, destination, options)} ${buildWorkspaceAgentGuidedVariantRenameInstruction(renameSuffix)}`;

export const buildWorkspaceAgentGuidedVariantSaveInstructionNodes = (
  destination: WorkspaceAgentGuidedVariantSaveDestination,
): JSONContent[] => {
  const boardPill =
    destination.type === 'versions'
      ? undefined
      : {
          boardId: destination.boardId,
          label: destination.type === 'board' ? destination.boardTitle : FALLBACK_BOARD_LABEL,
          pillId: uuidv4(),
        };

  return buildWorkspaceAgentComposerSeedContent({
    boardPill,
    prompt: ` ${getWorkspaceAgentGuidedVariantSaveInstruction(destination)}`,
  }).flatMap((paragraph) => paragraph.content ?? []);
};
